import type { IGenericUser } from '@/interfaces';
import { namedStudentFirstNames } from '@/utils/students.utils';
import { joinWithConjunction } from 'common/utils/string.utils';
import { useI18n } from 'vue-i18n';

export interface IStudentRecipientLabel {
  /** "+n weitere" alongside a focused student, rendered less prominently than `label`. */
  additionalLabel: string | null;
  /** `labelParts` joined as a human list. */
  label: string;
  /** Names or a count, so callers can style the parts and separators individually. */
  labelParts: string[];
}

export interface IStudentRecipientLabelParams {
  /** Names only this student; the remaining `students` become "+n weitere". */
  focusedStudent?: IGenericUser | null;
  students: IGenericUser[];
}

export interface IUseStudentRecipientLabel {
  getStudentRecipientLabel: (params: IStudentRecipientLabelParams) => IStudentRecipientLabel;
}

/**
 * The single rule for naming students: the focused one alone, else first names up to three, else a
 * count. Shared by the avatar summary and text-only surfaces so both read the same.
 */
export const useStudentRecipientLabel = (): IUseStudentRecipientLabel => {
  const { t } = useI18n();

  const getStudentRecipientLabel = ({
    focusedStudent = null,
    students,
  }: IStudentRecipientLabelParams): IStudentRecipientLabel => {
    const namedStudents = focusedStudent ? [focusedStudent] : students;
    const additionalStudentCount = focusedStudent ? Math.max(0, students.length - 1) : 0;
    const labelParts = namedStudentFirstNames(namedStudents) ?? [t('general.studentCount', { count: students.length })];

    return {
      additionalLabel:
        additionalStudentCount > 0 ? t('general.additionalStudentCount', { count: additionalStudentCount }) : null,
      label: joinWithConjunction(labelParts, t('general.and')),
      labelParts,
    };
  };

  return { getStudentRecipientLabel };
};
